import type { CourseSessionView } from "~/business-areas/course-session/course-session.model";

const registrationLinkFlow = {
  COOKIE_NAME: "registration_token",
  save(registration_token?: string) {
    useCookie(this.COOKIE_NAME).value = registration_token;
  },
  isActive(registration_token?: string) {
    return (
      useCurrentUser().me.value &&
      registration_token &&
      registration_token === useCookie(this.COOKIE_NAME).value
    );
  },
  erase() {
    useCookie(this.COOKIE_NAME).value = null;
  },
};

export function useRegistrationLink({
  session,
  registration_token,
}: {
  session: CourseSessionView;
  registration_token?: string;
}) {
  const route = useRoute();
  const { refresh, me } = useEnrichedCurrentUser();
  const onboarding = useOnboarding();
  const { $analytics } = useNuxtApp();

  const { status, execute } = useFetch(
    `/api/courses/${session.course_id}/sessions/${session.id}/register`,
    {
      method: "POST",
      body: {
        registration_token,
      },
      immediate: false,
      onResponse({ response }) {
        if (!response.ok)
          return showErrorToastNotification(response.statusText);

        useDialog("postRegistrationLinkDialog").open({
          session,
        });
        $analytics.track("registration_link_used");
        registrationLinkFlow.erase();

        return refresh();
      },
    },
  );

  async function register() {
    registrationLinkFlow.save(registration_token);

    if (me.value && me.value.company_name && me.value.job_title) {
      return execute();
    } else if (me.value) {
      // Updating company name on user profile to avoid having to ask for it on the onboarding
      await useEnrichedCurrentUser().update({
        company_name: me.value.company_name ?? session.organization?.name,
      });

      return onboarding.start({
        origin: "registration",
        personalInformationOnly: true,
        showToastNotificationOnComplete: false,
        onComplete: () => {
          execute();
        },
      });
    }

    return navigateTo(`/auth/login?redirect=${route.fullPath}`);
  }

  return {
    registrationLinkFlow,
    register,
    status,
  };
}
